import { topmostOverlay } from '@bookora/ui';

import { useSessionStore } from '@/auth/token-store';
import type { GuidanceStateResponse } from '@/api/generated/model';

import { canAutoOffer, canOfferHelp } from './eligibility';
import { findItem, toProfileState } from './state';
import { readHelpBackoff, readLastOffer, tabGuardAllows, tabHelpAllows } from './storage';
import { selectBusy, useTourStore } from './tour-store';
import type { TourDef } from './tours/types';

/**
 * Whether something on top of the page would make a proactive surface an
 * interruption: guidance busy, a tip card already in this visit, a foreign
 * overlay (an inline tour's own host drawer is not "on top"), a hidden tab.
 */
function blockedNow(ownHost: HTMLElement | null): boolean {
  const store = useTourStore.getState();
  const overlay = topmostOverlay();
  const foreignOverlay =
    overlay !== null &&
    !(ownHost && (overlay === ownHost || ownHost.contains(overlay) || overlay.contains(ownHost)));
  return (
    selectBusy(store) ||
    store.proactiveSurfaceOnPath === store.pathname ||
    foreignOverlay ||
    (typeof document !== 'undefined' && document.visibilityState !== 'visible')
  );
}

/**
 * Whether `tour` may be offered right now, read FRESH from every source (the
 * store, storage, the DOM) — called once before waiting for quiet and again
 * after, because anything may have happened in between.
 */
export function mayAutoOfferNow(
  tour: TourDef,
  data: GuidanceStateResponse | undefined,
  usableSteps: number,
  opts: { ownHost?: HTMLElement | null } = {},
): boolean {
  if (!data) return false;
  const session = useSessionStore.getState();
  const now = new Date();
  return canAutoOffer(tour, {
    now,
    profile: toProfileState(data.profile),
    impersonating: Boolean(session.impersonatedBy) || data.readOnly,
    localLastOffer: readLastOffer(),
    tabGuardAllows: tabGuardAllows(now),
    item: findItem(data, 'TOUR', tour.key),
    usableSteps,
    blocked: blockedNow(opts.ownHost ?? null),
  });
}

/**
 * Whether a HELP offer (critic C6) may appear right now for `context` — the
 * same fresh read as `mayAutoOfferNow`, through `canOfferHelp`: the ONE
 * budget, the person's switch, this tab's help guard and the context's own
 * backoff; never while impersonating or while the clinic is billing read-only.
 */
export function mayOfferHelpNow(
  data: GuidanceStateResponse | undefined,
  context: string,
  opts: { ownHost?: HTMLElement | null } = {},
): boolean {
  if (!data) return false;
  const session = useSessionStore.getState();
  const now = new Date();
  const access = session.activeClinicAccess;
  const clinicReadOnly =
    access !== null && access.clinicId === session.activeClinicId && !access.writesAllowed;
  return canOfferHelp({
    now,
    profile: toProfileState(data.profile),
    impersonating: Boolean(session.impersonatedBy) || data.readOnly,
    localLastOffer: readLastOffer(),
    clinicReadOnly,
    blocked: blockedNow(opts.ownHost ?? null),
    tabHelpAllows: tabHelpAllows(now),
    backoff: readHelpBackoff(session.user?.id ?? null, session.activeClinicId, context),
  });
}
